import { useContract, useShows, useUpdateContract } from '../../api/hooks';
import { Button, useToast } from '../../components/DesignSystem';
import { errorMessage } from '../../lib/errors';
import { RightsStepSection } from './RightsStepSection';

interface StepRightsInvoicingProps {
  contractId: string;
  onBack: () => void;
  onNext: () => void;
}

/** Step 3 — grant rights and configure invoicing for this contract. */
export function StepRightsInvoicing({ contractId, onBack, onNext }: StepRightsInvoicingProps) {
  const { showToast } = useToast();
  const contract = useContract(contractId);
  const shows = useShows({ limit: 100 });
  const updateContract = useUpdateContract();

  const show = shows.data?.data.find((entry) => entry.id === contract.data?.showId);
  const season = show?.seasons.find((entry) => entry.id === contract.data?.seasonId);

  async function handleTogglePriorState(checked: boolean) {
    try {
      await updateContract.mutateAsync({ contractId, input: { priorStateImported: checked } });
    } catch (error) {
      showToast(errorMessage(error, 'Could not update prior-state setting'), 'error');
    }
  }

  if (contract.data?.showId === undefined || contract.data?.showId === null) {
    return (
      <>
        <p className="rfp-notice" role="status">
          Select a show on the previous step before granting rights.
        </p>
        <div className="rfp-wizard-actions">
          <Button onClick={onBack} type="button" variant="secondary">
            Back
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <RightsStepSection seasonId={contract.data.seasonId} showId={contract.data.showId} />

      <section className="rfp-card">
        <div className="rfp-card__head">
          <div>
            <h2 className="rfp-card__title">Invoicing</h2>
            <p className="rfp-card__caption">
              Approve the primary terms on the next step to enable invoicing for this contract.
              Invoices created here are scoped to {show?.name ?? 'this show'}
              {season ? ` — ${season.seasonCode}` : ''} and feed into the ERP-ready invoicing
              pipeline.
            </p>
          </div>
        </div>

        <label className="rfp-check">
          <input
            checked={contract.data?.priorStateImported ?? false}
            onChange={(event) => handleTogglePriorState(event.target.checked)}
            type="checkbox"
          />
          Implementation — Import Prior State
        </label>
        <p className="rfp-muted-text" style={{ marginTop: 8 }}>
          Optional. Use when this contract is going live mid-life. Imported prior sales/costs are
          sealed as a locked prior period; the next real upload appears in the Movement column
          automatically.
        </p>
      </section>

      <div className="rfp-wizard-actions">
        <Button onClick={onBack} type="button" variant="secondary">
          Back
        </Button>
        <Button onClick={onNext} type="button">
          Continue <span aria-hidden="true">→</span>
        </Button>
      </div>
    </>
  );
}
